import { Button, Icon, Typography } from '@epilot/concorde-elements'
import type { GetAgPrice } from '@epilot/journey-logic-commons'
import {
  DEFAULT_CURRENCY,
  type Currency,
  type TimeFrequency
} from '@epilot/pricing'
import type { CompositePriceItem, PriceItem } from '@epilot/pricing-client'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MarkupCostsTable } from './MarkupCostsTable'
import { MeterCostsTable } from './MeterCostsTable'
import { NetworkOperatingCostsTable } from './NetworkOperatingCostsTable'
import { OtherCostsTable } from './OtherCostsTable'
import classes from './PriceGetAgDetails.module.scss'
import { PriceGetAgDetailsProvider } from './PriceGetAgDetailsContext'
import { getTaxValueFromPriceItem, getUnitFromPriceItem } from './utils'

type PriceGetAgDetailsProps = {
  item: PriceItem | CompositePriceItem
  getAgPriceDetails: GetAgPrice
  openByDefault?: boolean
}

export const PriceGetAgDetails = ({
  item,
  getAgPriceDetails,
  openByDefault
}: PriceGetAgDetailsProps) => {
  const [isExpanded, setIsExpanded] = useState(openByDefault)
  const { t } = useTranslation()

  const contextValue = useMemo(() => {
    const userLocale = navigator.language || 'de-DE'
    const currency = (getAgPriceDetails.currency ||
      DEFAULT_CURRENCY) as Currency
    const billingPeriod = getAgPriceDetails.billing_period as TimeFrequency
    const unit = getUnitFromPriceItem(item)
    const tax = getTaxValueFromPriceItem(item)
    // TODO: get the unit price period from custom configuration
    const unitPricePeriod: TimeFrequency = 'monthly'

    const getAgPriceType = getAgPriceDetails.inputs.type

    return {
      item,
      getAgPriceDetails,
      userLocale,
      currency,
      billingPeriod,
      unit,
      unitPricePeriod,
      tax,
      getAgPriceType: getAgPriceType as 'power' | 'gas',
      t
    }
  }, [item, getAgPriceDetails, t])

  return (
    <>
      <div className={classes.priceGetAgDetailsContainer}>
        <Button
          aria-expanded={isExpanded}
          aria-label={t(
            isExpanded
              ? 'getag_details_table.close'
              : 'getag_details_table.open'
          )}
          className={classes.priceGetAgDetailsExpandButton}
          data-testid={'getag_details_table-toggle-button'}
          id={`getag_details_table-${item._product?._id}-${item._price?._id}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography as="h6" className={classes.heading}>
            {isExpanded
              ? t('getag_details_table.close')
              : t('getag_details_table.open')}
          </Typography>

          <Icon
            className={classes.icon}
            name={isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            size="20px"
          />
        </Button>
      </div>
      <div
        className={`${classes.expandableContent} ${isExpanded ? classes.expanded : classes.collapsed}`}
      >
        <div className={classes.priceGetAgDetailsContent}>
          <PriceGetAgDetailsProvider value={contextValue}>
            <MarkupCostsTable />
            <NetworkOperatingCostsTable />
            <MeterCostsTable />
            <OtherCostsTable />
          </PriceGetAgDetailsProvider>
        </div>
      </div>
    </>
  )
}
